import { exponentOf, currencyMeta } from './currency';
const SAFE = Number.MAX_SAFE_INTEGER;
function assertSafe(minor, what) {
    if (!Number.isFinite(minor)) {
        throw new Error(`${what} produced a non-finite amount`);
    }
    if (!Number.isInteger(minor)) {
        throw new Error(`${what} produced a fractional minor-unit amount (${minor})`);
    }
    if (Math.abs(minor) > SAFE) {
        throw new Error(`${what} exceeded safe integer range - amount is not representable`);
    }
    return minor;
}
function assertSameCurrency(a, b) {
    if (a.currency !== b.currency) {
        throw new Error(`Cannot combine ${a.currency} and ${b.currency}. Convert to a common ` +
            'currency with an explicit exchange rate first.');
    }
}
/**
 * Round a fractional minor-unit value to an integer, half away from zero.
 *
 * `Math.round` is half-UP (toward +Infinity), so it rounds -0.5 to 0 and +0.5
 * to 1 - asymmetric, which means a credit note and the invoice it reverses can
 * differ by one minor unit. Half-away-from-zero keeps |round(-x)| === |round(x)|,
 * so reversing a document always nets to exactly zero.
 */
export function roundHalfAwayFromZero(value) {
    return value < 0 ? -Math.round(-value) : Math.round(value);
}
/**
 * Parse a decimal string (or number) into Money.
 *
 * Accepts "1234.5", "1,234.50", "₹1234.50", " 1234 ", "-12.34", "". Rejects
 * anything else loudly - a silently-zero amount is worse than a 400.
 */
export function parse(value, currency) {
    const exp = exponentOf(currency);
    if (value === null || value === undefined || value === '') {
        return { minor: 0, currency };
    }
    if (typeof value === 'number') {
        // Tolerated for literals in code and for JSON that has already lost the
        // string; scaling before rounding keeps a value like 1234.565 honest.
        return { minor: assertSafe(roundHalfAwayFromZero(value * 10 ** exp), 'parse'), currency };
    }
    const cleaned = value.replace(/[\s,_]/g, '').replace(/[^0-9.+-]/g, '');
    // Deliberately no exponent notation. The scaling below is string-based to
    // keep precision, and "1.5e3" cannot be scaled by moving a decimal point.
    // A caller holding an exponent-form number should pass it as a number.
    if (!/^[+-]?(\d+(\.\d*)?|\.\d+)$/.test(cleaned)) {
        throw new Error(`"${value}" is not a valid amount`);
    }
    const negative = cleaned.startsWith('-');
    const [intPart = '0', fracPartRaw = ''] = cleaned.replace(/^[+-]/, '').split('.');
    // String-based scaling, so a value carrying more precision than the currency
    // supports is scaled exactly and rounded once, never passed through a float.
    const frac = fracPartRaw.padEnd(exp + 1, '0');
    const kept = frac.slice(0, exp);
    const nextDigit = Number(frac[exp] ?? '0');
    // Half away from zero: the sign is reapplied below, so on this absolute
    // value a tie always rounds up. Digits beyond the tie cannot change that -
    // anything above a half is already up, anything below is already down.
    let minor = Number(intPart) * 10 ** exp + Number(kept || '0');
    if (nextDigit >= 5)
        minor += 1;
    return { minor: assertSafe(negative ? -minor : minor, 'parse'), currency };
}
/** Money from an integer count of minor units. */
export function fromMinor(minor, currency) {
    return { minor: assertSafe(minor, 'fromMinor'), currency };
}
export function zero(currency) {
    return { minor: 0, currency };
}
/**
 * Render as a plain decimal string for storage or the wire: "1234.50".
 * No symbol, no grouping, always exactly `exponent` decimal places - this is
 * what goes into a DECIMAL(15,2) column and into JSON.
 */
export function format(m) {
    const exp = exponentOf(m.currency);
    const sign = m.minor < 0 ? '-' : '';
    const abs = Math.abs(m.minor).toString().padStart(exp + 1, '0');
    if (exp === 0)
        return sign + abs;
    return `${sign}${abs.slice(0, -exp)}.${abs.slice(-exp)}`;
}
/** Human display with symbol and locale grouping: "₹5,00,000.00". */
export function formatHuman(m, opts = {}) {
    const meta = currencyMeta(m.currency);
    return new Intl.NumberFormat(meta.locale, {
        style: opts.showSymbol === false ? 'decimal' : 'currency',
        currency: meta.code,
        minimumFractionDigits: meta.exponent,
        maximumFractionDigits: meta.exponent,
    }).format(m.minor / 10 ** meta.exponent);
}
// ── arithmetic ─────────────────────────────────────────────────────────────
export function add(...amounts) {
    if (amounts.length === 0)
        throw new Error('add() needs at least one amount');
    const [first, ...rest] = amounts;
    let total = first.minor;
    for (const m of rest) {
        assertSameCurrency(first, m);
        total += m.minor;
    }
    return { minor: assertSafe(total, 'add'), currency: first.currency };
}
export function subtract(a, b) {
    assertSameCurrency(a, b);
    return { minor: assertSafe(a.minor - b.minor, 'subtract'), currency: a.currency };
}
export function negate(m) {
    return { minor: -m.minor, currency: m.currency };
}
export function abs(m) {
    return { minor: Math.abs(m.minor), currency: m.currency };
}
/**
 * Multiply by a plain factor (a quantity, or an exchange rate).
 *
 * Rounds ONCE, at the end, half away from zero. Callers that need the
 * unrounded intermediate - a line total feeding a document total - should use
 * `multiplyExact` and round at the boundary they actually care about.
 */
export function multiply(m, factor) {
    if (!Number.isFinite(factor))
        throw new Error('multiply() needs a finite factor');
    return { minor: assertSafe(roundHalfAwayFromZero(m.minor * factor), 'multiply'), currency: m.currency };
}
/** Unrounded minor-unit product, for chaining before a single final rounding. */
export function multiplyExact(m, factor) {
    return m.minor * factor;
}
/** `percent` is a percentage, so `percentOf(x, 18)` is 18% of x. */
export function percentOf(m, percent) {
    return multiply(m, percent / 100);
}
export function divide(m, divisor) {
    if (!divisor)
        throw new Error('divide() by zero');
    return { minor: assertSafe(roundHalfAwayFromZero(m.minor / divisor), 'divide'), currency: m.currency };
}
// ── comparison ─────────────────────────────────────────────────────────────
export function compare(a, b) {
    assertSameCurrency(a, b);
    return a.minor < b.minor ? -1 : a.minor > b.minor ? 1 : 0;
}
export const isZero = (m) => m.minor === 0;
export const isPositive = (m) => m.minor > 0;
export const isNegative = (m) => m.minor < 0;
export const equals = (a, b) => compare(a, b) === 0;
export const greaterThan = (a, b) => compare(a, b) === 1;
export const lessThan = (a, b) => compare(a, b) === -1;
export const min = (a, b) => (compare(a, b) <= 0 ? a : b);
export const max = (a, b) => (compare(a, b) >= 0 ? a : b);
/**
 * Split an amount into `n` parts that sum EXACTLY back to it.
 *
 * ₹100 into 3 is not ₹33.33 three times - that loses a paise. This gives
 * [33.34, 33.33, 33.33], distributing the remainder one minor unit at a time
 * from the front. Used wherever a total is apportioned: a document-level
 * discount pushed down onto lines, a payment spread across invoices.
 */
export function allocate(m, n) {
    if (!Number.isInteger(n) || n <= 0)
        throw new Error('allocate() needs a positive integer count');
    const base = Math.trunc(m.minor / n);
    let remainder = m.minor - base * n;
    const step = remainder < 0 ? -1 : 1;
    return Array.from({ length: n }, () => {
        let share = base;
        if (remainder !== 0) {
            share += step;
            remainder -= step;
        }
        return { minor: share, currency: m.currency };
    });
}
/**
 * Split an amount by weights, summing EXACTLY back to it.
 *
 * This is the one that matters for invoicing: apportioning a ₹100 document
 * discount across lines of ₹700 and ₹300 must give ₹70 and ₹30, and whatever
 * the weights, the parts must still total the original. The largest-remainder
 * method does that - allocate the floors, then hand the leftover minor units
 * to whichever parts were rounded down hardest.
 *
 * Zero total weight splits evenly rather than throwing: a discount on a
 * zero-value document is legitimate, and refusing it would block a save.
 */
export function allocateByWeights(m, weights) {
    if (weights.length === 0)
        throw new Error('allocateByWeights() needs at least one weight');
    if (weights.some((w) => w < 0))
        throw new Error('allocateByWeights() needs non-negative weights');
    const totalWeight = weights.reduce((s, w) => s + w, 0);
    if (totalWeight === 0)
        return allocate(m, weights.length);
    const exact = weights.map((w) => (m.minor * w) / totalWeight);
    const floors = exact.map((x) => Math.trunc(x));
    let remainder = m.minor - floors.reduce((s, x) => s + x, 0);
    const step = remainder < 0 ? -1 : 1;
    const order = exact
        .map((x, i) => ({ i, frac: Math.abs(x - floors[i]) }))
        .sort((a, b) => b.frac - a.frac || a.i - b.i);
    const out = floors.slice();
    for (const { i } of order) {
        if (remainder === 0)
            break;
        out[i] += step;
        remainder -= step;
    }
    return out.map((minor) => ({ minor, currency: m.currency }));
}
/**
 * Convert between currencies at an explicit rate.
 *
 * The rate is always "how many units of `to` for one unit of `from`", and it is
 * ALWAYS passed in - never looked up in here. An exchange rate is a fact about
 * a moment in time that belongs on the document it was applied to; a helper
 * that fetched today's rate would silently restate last year's invoices.
 */
export function convert(m, to, rate) {
    if (!Number.isFinite(rate) || rate <= 0)
        throw new Error('convert() needs a positive finite rate');
    const fromExp = exponentOf(m.currency);
    const toExp = exponentOf(to);
    const major = m.minor / 10 ** fromExp;
    return { minor: assertSafe(roundHalfAwayFromZero(major * rate * 10 ** toExp), 'convert'), currency: to };
}
//# sourceMappingURL=money.js.map